import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Button, Col, Container, Row, Spinner, Stack } from "react-bootstrap";
import { currentUrl, unsavedChanges } from "../../../App";
import { fetchDwellings, fetchRooms, postRoom, fetchBeds, postBed, fetchDwellingTypes } from "../../../services/httpClient";
import PageTitle from "../../ui/PageTitle";
import useApp from "../../../utils/useApp";
import DwellingManagementCard from "./DwellingManagementCard";
import RoomManagementCard from "./RoomManagementCard";
import BedManagement from "./BedManagement";
import { bedsOfRoom, propertyCounts } from "../../../helpers/housingAssignment";
import BulkPropertySetupForm from "./BulkPropertySetupForm";

interface DwellingManagementState {
  setOrgToSave: any;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
}

const OrgDwellingManagement = ({ setOrgToSave, setShowToast, setToastText, setToastBody }: DwellingManagementState) => {
  const location = useLocation();
  currentUrl.value = location.pathname;
  const { auth }: any = useApp();
  const [errMsg, setErrMsg] = useState("");
  const [dwellings, setDwellings] = useState<any[]>([]);
  const [selectedDwelling, setSelectedDwelling] = useState<any>(null);
  const [rooms, setRooms] = useState<any[]>([]);
  const [selectedRoom, setSelectedRoom] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddDwelling, setShowAddDwelling] = useState(false);
  const [dwellingTypes, setDwellingTypes] = useState<any[]>([]);

  useEffect(() => {
    if (auth?.profile.includeHMIS === 1) {
      getDwellings();
    }
  }, [auth?.profile]);

  useEffect(() => {
    fetchDwellingTypes()
      .then((res: any) => {
        // SelectObject binds its value to each option's `key` field, but DwellingController
        // expects dwelling_type_id as a raw numeric id, not the lookup's own `key` slug -
        // remap so the select's value is actually the id.
        const types = (res[0]?.data || []).map((t: any) => ({ ...t, key: t.id }));
        setDwellingTypes(types);
      })
      .catch((err: any) => {
        console.log(err);
      });
  }, []);

  const getDwellings = () => {
    fetchDwellings()
      .then((res: any) => {
        const dwellingList = res[0].data;
        return fetchRooms().then((roomRes: any) => {
          const roomList = roomRes[0].data;
          return fetchBeds().then((bedRes: any) => {
            const bedList = bedRes[0].data;
            const withCounts = dwellingList.map((d: any) => {
              const { roomCount, bedCount } = propertyCounts(roomList, bedList, d.id);
              return { ...d, room_count: roomCount, bed_count: bedCount };
            });
            withCounts.sort((a: any, b: any) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" }));
            setDwellings(withCounts);
          });
        });
      })
      .catch((err: any) => {
        console.log(err);
      })
      .finally(() => {
        setIsLoading(false);
        setErrMsg("");
      });
  };

  const getRoomsForDwelling = (dwellingId: any) => {
    fetchRooms()
      .then((res: any) => {
        const roomList = res[0].data;

        return fetchBeds().then((bedRes: any) => {
          const bedList = bedRes[0].data;
          const scopedRooms = roomList
            .filter((r: any) => r.property_id === dwellingId)
            .map((r: any) => {
              const roomBeds = bedsOfRoom(bedList, r.id);
              return { ...r, bed_count: `${roomBeds.length} beds`, beds: roomBeds };
            });
          scopedRooms.sort((a: any, b: any) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" }));
          setRooms(scopedRooms);
          setSelectedRoom((current: any) => (current ? scopedRooms.find((r: any) => r.id === current.id) || null : null));
        });
      })
      .catch((err: any) => {
        console.log(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const onClickManageRooms = (dwelling: any) => {
    setIsLoading(true);
    setSelectedDwelling(dwelling);
    getRoomsForDwelling(dwelling.id);
  };

  const onClickBackToDwellings = () => {
    setSelectedDwelling(null);
    setRooms([]);
    setSelectedRoom(null);
    getDwellings();
  };

  const onClickAddDwelling = () => {
    setErrMsg("");
    setShowAddDwelling(true);
  };

  const onDwellingCreated = (dwellingName: string) => {
    setShowAddDwelling(false);
    setShowToast(true);
    setToastText("Dwelling Created");
    setToastBody(dwellingName);
    setOrgToSave(false);
    getDwellings();
  };

  const onCancelAddDwelling = () => {
    setShowAddDwelling(false);
    setOrgToSave(false);
  };

  const onClickAddRoom = () => {
    const roomName = rooms?.length ? `Room ${rooms?.length + 1}` : "Room 1";
    postRoom({ name: roomName, property_id: selectedDwelling?.id })
      .then(() => {
        setShowToast(true);
        setToastText("Room Created");
        setToastBody(roomName);
        unsavedChanges.value = false;
        setOrgToSave(false);
        getRoomsForDwelling(selectedDwelling?.id);
      })
      .catch((err: any) => setErrMsg(err?.response?.data?.msg));
  };

  const onClickAddBed = () => {
    setErrMsg("");
    const roomId = selectedRoom?.id;
    const bedName = selectedRoom?.beds?.length ? `Bed ${selectedRoom?.beds?.length + 1}` : "Bed 1";
    postBed({ room_id: roomId, name: bedName })
      .then(() => {
        setShowToast(true);
        setToastText("Bed Created");
        setToastBody(bedName);
        unsavedChanges.value = false;
        setOrgToSave(false);
        getRoomsForDwelling(selectedDwelling?.id);
      })
      .catch((err: any) => setErrMsg(err?.response?.data?.msg));
  };

  const getRoomsRefresh = () => getRoomsForDwelling(selectedDwelling?.id);

  return (
    <>
      {isLoading ? (
        <Container>
          <Button size="sm" className="spinner-button spinner-button-main">
            <Spinner animation="border" style={{ color: "#F6893D" }} />
          </Button>
        </Container>
      ) : (
        <>
          <p className={errMsg ? "errmsg" : "offscreen"} aria-live="assertive">
            {errMsg}
          </p>
          <PageTitle
            title={showAddDwelling ? "Create Dwelling" : selectedRoom ? selectedRoom.name : selectedDwelling ? selectedDwelling.name : "Dwellings"}
            type="section"
            subTitleText={
              !selectedDwelling && !showAddDwelling ? "Off-site houses and apartments assigned whole to one case at a time." : undefined
            }
            buttonTitle={showAddDwelling ? undefined : selectedRoom ? "Add Bed" : selectedDwelling ? "Add Room" : "Create Dwelling"}
            buttonColor={"primary"}
            onButtonClick={
              showAddDwelling
                ? undefined
                : selectedRoom
                  ? () => onClickAddBed()
                  : selectedDwelling
                    ? () => onClickAddRoom()
                    : () => onClickAddDwelling()
            }
            backButtonText={
              showAddDwelling
                ? "Back to Dwellings"
                : selectedRoom
                  ? `Back to ${selectedDwelling?.name}`
                  : selectedDwelling
                    ? "Back to Dwellings"
                    : undefined
            }
            onBackClick={
              showAddDwelling
                ? () => onCancelAddDwelling()
                : selectedRoom
                  ? () => setSelectedRoom(null)
                  : selectedDwelling
                    ? () => onClickBackToDwellings()
                    : null
            }
          />

          {showAddDwelling ? (
            <BulkPropertySetupForm
              kind="dwelling"
              existingNames={dwellings.map((d: any) => d.name)}
              dwellingTypes={dwellingTypes}
              onCreated={onDwellingCreated}
              onCancel={onCancelAddDwelling}
              setErrMessage={setErrMsg}
            />
          ) : selectedRoom ? (
            <BedManagement
              getItems={getRoomsRefresh}
              beds={selectedRoom?.beds || []}
              setOrgToSave={setOrgToSave}
              setErrMessage={setErrMsg}
              setShowToast={setShowToast}
              setToastText={setToastText}
              setToastBody={setToastBody}
            />
          ) : selectedDwelling ? (
            <>
              <Row className="mt-3">
                <Col xs={12} lg={10}>
                  {rooms.length === 0 ? (
                    <p className="mt-2">No Rooms Created</p>
                  ) : (
                    <Stack gap={2}>
                      {rooms?.map((room: any, index: number) => (
                        <Row key={index}>
                          <Col xs={12}>
                            <RoomManagementCard
                              room={room}
                              getItems={getRoomsRefresh}
                              setSelectedRoom={setSelectedRoom}
                              setOrgToSave={setOrgToSave}
                              setErrMessage={setErrMsg}
                              setShowToast={setShowToast}
                              setToastText={setToastText}
                              setToastBody={setToastBody}
                            />
                          </Col>
                        </Row>
                      ))}
                    </Stack>
                  )}
                </Col>
              </Row>
            </>
          ) : (
            <Row className="mt-3">
              <Col xs={12} lg={10}>
                {dwellings.length === 0 ? (
                  <p className="mt-2">No Dwellings Created</p>
                ) : (
                  <Stack gap={2}>
                    {dwellings?.map((dwelling: any, index: number) => (
                      <Row key={index}>
                        <Col xs={12}>
                          <DwellingManagementCard
                            dwelling={dwelling}
                            dwellingTypes={dwellingTypes}
                            getItems={getDwellings}
                            onManageRooms={onClickManageRooms}
                            setOrgToSave={setOrgToSave}
                            setErrMessage={setErrMsg}
                            setShowToast={setShowToast}
                            setToastText={setToastText}
                            setToastBody={setToastBody}
                          />
                        </Col>
                      </Row>
                    ))}
                  </Stack>
                )}
              </Col>
            </Row>
          )}

          <Row className="mb-5"></Row>
        </>
      )}
    </>
  );
};

export default React.memo(OrgDwellingManagement);
